import type { DictationStatus } from "#features/conversations/hooks/useDictation.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { Button, Tooltip, TooltipTrigger } from "@hopper-ui/components";
import { IconMic, IconMicFill } from "@intentui/icons";
import classNames from "classnames";
import { useEffect, useRef } from "react";
import "./ConversationDictationButton.css";

interface Props {
    status: DictationStatus;
    audioStream: MediaStream | null;
    isDisabled: boolean;
    shortcutHint: string;
    onToggle: () => void;
}

// Drives the listening glow with the real microphone level — the button visibly reacts to the user's
// voice, doubling as live confirmation that dictation is capturing audio. Reads from the recognition
// session's own MediaStream: the microphone is never opened a second time for the meter.
function useVoiceLevel(stream: MediaStream | null, elementRef: React.RefObject<HTMLButtonElement | null>) {
    useEffect(() => {
        const element = elementRef.current;
        if (!stream || !element) {
            return;
        }

        let frame = 0;
        const audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        audioContext.createMediaStreamSource(stream).connect(analyser);
        const samples = new Uint8Array(analyser.frequencyBinCount);

        const tick = () => {
            analyser.getByteTimeDomainData(samples);
            let sum = 0;
            for (const sample of samples) {
                const deviation = (sample - 128) / 128;
                sum += deviation * deviation;
            }
            const level = Math.min(1, Math.sqrt(sum / samples.length) * 4);
            element.style.setProperty("--wai-voice-level", level.toFixed(3));
            frame = requestAnimationFrame(tick);
        };
        tick();

        return () => {
            cancelAnimationFrame(frame);
            // Close only the analyser context — the stream belongs to the recognition session.
            void audioContext.close();
            element.style.removeProperty("--wai-voice-level");
        };
    }, [stream, elementRef]);
}

export function ConversationDictationButton({ status, audioStream, isDisabled, shortcutHint, onToggle }: Props) {
    const { tShared } = useSharedTranslation();
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const isActive = status === "listening" || status === "starting";

    useVoiceLevel(status === "listening" ? audioStream : null, buttonRef);

    const label = status === "error"
        ? tShared("dictationError")
        : isActive ? tShared("dictationStop") : tShared("dictationStart");

    return (
        <TooltipTrigger>
            <Button
                ref={buttonRef}
                size="sm"
                variant="ghost-secondary"
                borderRadius="circle"
                aria-label={label}
                aria-pressed={isActive}
                aria-keyshortcuts="Meta+D Control+D"
                type="button"
                className={classNames("wai-conversation-dictation-button", {
                    "wai-conversation-dictation-button--listening": status === "listening",
                    "wai-conversation-dictation-button--starting": status === "starting",
                    "wai-conversation-dictation-button--error": status === "error"
                })}
                isDisabled={isDisabled}
                onPress={onToggle}
            >
                {isActive ? <IconMicFill /> : <IconMic />}
            </Button>
            <Tooltip>{`${label} (${shortcutHint})`}</Tooltip>
        </TooltipTrigger>
    );
}
